import React, { FC, useState } from "react";
import { useQueries } from "react-query";
import axios from "axios";
import classes from "./BranchQueueTreeSelect.module.scss";
import { useGetBranches } from "../../../hooks/Admin/useGetBranches";
import { BranchAPI } from "../../../constants/API";
import { commonHeaders } from "../../../constants/HeadersAPI";
import type { BranchQueueItemDto } from "../../../hooks/Admin/WorkingPlacesTable/useGetQueuesForCurrentUser";

export type BranchQueueTreeSelectProps = {
  label?: string;
  selectedBranchIds: number[];
  selectedQueueIds: number[];
  onBranchChange: (ids: number[]) => void;
  onQueueChange: (ids: number[]) => void;
  className?: string;
  errorMessage?: string | null;
  /** Если задан, отображаются только филиалы с этими id (например, для администратора филиала) */
  branchIdsFilter?: number[];
  /** Если задан, отображаются только очереди с этими id. */
  queueIdsFilter?: number[];
  /** Если true, можно выбрать только одну очередь. */
  singleQueue?: boolean;
};

const BranchQueueTreeSelect: FC<BranchQueueTreeSelectProps> = ({
  label = "Привязка к филиалам и очередям",
  selectedBranchIds,
  selectedQueueIds,
  onBranchChange,
  onQueueChange,
  className,
  errorMessage = null,
  branchIdsFilter,
  queueIdsFilter,
  singleQueue = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { branches, isLoading: loadingBranches } = useGetBranches();

  const filteredBranches = branchIdsFilter?.length
    ? branches.filter((b) => branchIdsFilter.includes(b.id))
    : branches;

  const queueResults = useQueries(
    filteredBranches.map((b) => ({
      queryKey: ["branches", b.id, "queues"] as const,
      queryFn: () =>
        axios
          .get<BranchQueueItemDto[]>(BranchAPI.getQueues(b.id), commonHeaders())
          .then((r) => r.data),
      enabled: !!b.id,
    })),
  );

  const branchesWithQueues = filteredBranches.map((b, i) => {
    const rawQueues = queueResults[i]?.data ?? [];
    const filteredQueues =
      queueIdsFilter !== undefined
        ? rawQueues.filter((q) => queueIdsFilter.includes(q.id))
        : rawQueues;
    return {
      branch: b,
      queues: filteredQueues,
      isLoadingQueues: queueResults[i]?.isLoading ?? false,
    };
  });

  const branchesWithQueuesFiltered = branchesWithQueues.filter(({ queues }) =>
    queueIdsFilter !== undefined ? queues.length > 0 : true,
  );

  const toggleBranch = (id: number) => {
    if (selectedBranchIds.includes(id)) {
      onBranchChange(selectedBranchIds.filter((x) => x !== id));
    } else {
      onBranchChange([...selectedBranchIds, id]);
    }
  };

  const toggleQueue = (
    queueId: number,
    branchId: number,
    queueIdsInBranch: number[],
  ) => {
    if (selectedQueueIds.includes(queueId)) {
      const nextQueueIds = selectedQueueIds.filter((x) => x !== queueId);
      onQueueChange(nextQueueIds);
      if (!singleQueue) {
        const hasOtherSelectedInBranch = queueIdsInBranch.some(
          (id) => id !== queueId && nextQueueIds.includes(id),
        );
        if (!hasOtherSelectedInBranch && selectedBranchIds.includes(branchId)) {
          onBranchChange(selectedBranchIds.filter((x) => x !== branchId));
        }
      } else {
        onBranchChange([]);
      }
    } else {
      if (singleQueue) {
        onQueueChange([queueId]);
        onBranchChange([branchId]);
      } else {
        onQueueChange([...selectedQueueIds, queueId]);
        if (!selectedBranchIds.includes(branchId)) {
          onBranchChange([...selectedBranchIds, branchId]);
        }
      }
    }
  };

  const selectedQueueLabels = branchesWithQueuesFiltered.flatMap(
    ({ branch: b, queues }) =>
      queues
        .filter((q) => selectedQueueIds.includes(q.id))
        .map(
          (q) =>
            `${b.name || b.code || `Филиал ${b.id}`} — ${q.queueName || `Очередь ${q.id}`}`,
        ),
  );

  const triggerText =
    selectedQueueLabels.length === 0
      ? "Выберите филиалы и очереди"
      : selectedQueueLabels.length === 1
        ? selectedQueueLabels[0]
        : `Выбрано очередей: ${selectedQueueLabels.length}`;

  return (
    <div className={`${classes.wrap} ${className ?? ""}`}>
      {/* {label && <span className={classes.label}>{label}</span>} */}
      <div className={classes.select}>
        <button
          type="button"
          className={`${classes.trigger} ${errorMessage ? classes.triggerError : ""}`}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span className={classes.triggerText}>{triggerText}</span>
          <span
            className={`${classes.triggerArrow} ${isOpen ? classes.triggerArrowOpen : ""}`}
          />
        </button>
        {errorMessage && (
          <h3 className={classes.validationError}>{errorMessage}</h3>
        )}
        {isOpen && (
          <div className={classes.dropdown}>
            {loadingBranches ? (
              <span className={classes.hint}>Загрузка...</span>
            ) : branchesWithQueuesFiltered.length === 0 ? (
              <span className={classes.hint}>Нет доступных филиалов</span>
            ) : (
              <div className={classes.treeList}>
                {branchesWithQueuesFiltered.map(
                  ({ branch: b, queues, isLoadingQueues }) => (
                    <div key={b.id} className={classes.branchGroup}>
                      <div className={classes.branchItem}>
                        <span>{b.name || b.code || `Филиал ${b.id}`}</span>
                      </div>
                      <div className={classes.queueSubList}>
                        {isLoadingQueues ? (
                          <span className={classes.queueHint}>
                            Загрузка очередей...
                          </span>
                        ) : queues.length === 0 ? (
                          <span className={classes.queueEmpty}>
                            Нет очередей
                          </span>
                        ) : (
                          queues.map((q) => (
                            <label key={q.id} className={classes.queueItem}>
                              <input
                                type="checkbox"
                                checked={selectedQueueIds.includes(q.id)}
                                onChange={() =>
                                  toggleQueue(
                                    q.id,
                                    b.id,
                                    queues.map((qu) => qu.id),
                                  )
                                }
                              />
                              <span>{q.queueName || `Очередь ${q.id}`}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BranchQueueTreeSelect;
